import { ReactNode, useEffect, useState } from 'react'

import {
    AlertProps,
    BatteryInfoAlert,
    BatteryWelcomeAlert,
    InvalidCredentialsAlert,
    MonitoringMonthlyReportAlert,
    TeslaAnnouncementAlert,
    WelcomeAlert
} from '@/components'
import { BannerIdEnum } from '@/enums'
import { useAuth } from '@/hooks'
import { WarrantySummaryItem } from '@/models'
import { feature, isApiCancelError, sessionStorage } from '@/services'

interface BannerProps extends AlertProps {
    allowedBanners: BannerIdEnum[]
}

type BannerDataType = {
    id: BannerIdEnum
    showIf?: boolean
    banner: ReactNode
}

export const Banner = ({
    allowedBanners,
    ...props
}: BannerProps) => {
    const [abortController, setAbortController] = useState<AbortController | null>(null)
    const [seenBanners, setSeenBanners] = useState<BannerIdEnum[]>(
        sessionStorage.get('seen_banners') as BannerIdEnum[] || []
    )
    const [isInvoiced, setIsInvoiced] = useState(false)
    const [processing, setProcessing] = useState(true)
    const auth = useAuth()

    const fetchIsInvoiced = async (controller: AbortController) => {
        if (allowedBanners.includes(BannerIdEnum.WELCOME) && auth.user?.role.isCertifiedProvider) {
            const { meta } = await WarrantySummaryItem.index({}, { signal: controller.signal })
            setIsInvoiced(!!meta?.overall_count)
        }
    }

    const fetchData = () => {
        const controller = new AbortController
        setAbortController(controller)
        try {
            fetchIsInvoiced(controller)
        } catch (err) {
            if (!isApiCancelError(err)) {
                throw err
            }
        } finally {
            setProcessing(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => () => {
        abortController?.abort('canceled')
    }, [abortController])

    const handleClose = (banner: BannerIdEnum) => {
        const seenBanners = sessionStorage.get('seen_banners') as BannerIdEnum[] || []
        seenBanners.push(banner)
        setSeenBanners(seenBanners)
        sessionStorage.set('seen_banners', seenBanners)
    }

    if (processing) return null

    const banners: BannerDataType[] = [
        {
            id: BannerIdEnum.TESLA_ANNOUNCEMENT_A,
            showIf: feature('tesla-announcement'),
            banner: <TeslaAnnouncementAlert
                variant="a"
                onClose={() => handleClose(BannerIdEnum.TESLA_ANNOUNCEMENT_A)}
                {...props}
            />
        },
        {
            id: BannerIdEnum.TESLA_ANNOUNCEMENT_B,
            showIf: feature('tesla-announcement'),
            banner: <TeslaAnnouncementAlert
                variant="b"
                {...props}
            />
        },
        {
            id: BannerIdEnum.TESLA_ANNOUNCEMENT_C,
            showIf: feature('tesla-announcement'),
            banner: <TeslaAnnouncementAlert
                variant="c"
                {...props}
            />
        },
        {
            id: BannerIdEnum.MONITORING_MONTHLY_REPORT,
            showIf: feature('monitoring-alert') &&
                auth.user?.role.isCertifiedProvider,
            banner: <MonitoringMonthlyReportAlert
                onClose={() => handleClose(BannerIdEnum.MONITORING_MONTHLY_REPORT)}
                {...props}
            />
        },
        {
            id: BannerIdEnum.WELCOME,
            showIf: auth.user?.role.isCertifiedProvider &&
                !auth.user?.company.integrations.length &&
                !isInvoiced,
            banner: <WelcomeAlert {...props}/>
        },
        {
            id: BannerIdEnum.INVALID_CREDENTIALS,
            showIf: auth.user?.role.isCertifiedProvider &&
            (auth.user?.company.monitoringStatus.isInvalid ||
                auth.user?.company.monitoringStatus.isMissing ||
                auth.user?.company.monitoringStatus.isIncomplete),
            banner: <InvalidCredentialsAlert
                onClose={() => handleClose(BannerIdEnum.INVALID_CREDENTIALS)}
                {...props}
            />
        },
        {
            id: BannerIdEnum.BATTERY_INFO,
            showIf: auth.user?.role.isCertifiedProvider &&
                !auth.user?.company.eligibleProductTypes.some(item => item.hasStorage),
            banner: <BatteryInfoAlert
                onClose={() => handleClose(BannerIdEnum.BATTERY_INFO)}
                {...props}
            />
        },
        {
            id: BannerIdEnum.BATTERY_WELCOME,
            showIf: auth.user?.role.isCertifiedProvider &&
                auth.user?.company.eligibleProductTypes.some(item => item.hasStorage),
            banner: <BatteryWelcomeAlert
                onClose={() => handleClose(BannerIdEnum.BATTERY_WELCOME)}
                {...props}
            />
        }
    ]

    const item = banners.find(item =>
        item.showIf &&
        !seenBanners.includes(item.id) &&
        allowedBanners.includes(item.id)
    )

    return item?.banner
}
